import { useTranslation } from "@helpers/useTranslation";
import { FC, useEffect, useMemo } from "react";
import { sortTeamsByPoints } from "src/helpers/sortTeams";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useTeamId } from "src/helpers/useTeam";
import { TeamRow } from "./TeamRow";

type Props = {
  compact?: boolean;
  limit?: number;
  className?: string;
};

export const HighscoreTable: FC<Props> = ({ compact, limit, className }) => {
  const { t } = useTranslation("highscores");
  const eventId = useEventId();
  const [event, loadingEvent, errorEvent] = useEvent();
  const teamId = useTeamId();
  const [teams] = useSimpleCollection<Team>(`events/${eventId}/teams`);
  const teamsWithoutUploaders = useMemo(() => teams?.filter((team) => !team.isUploader), [teams]);
  const sortedTeams = useMemo(() => teamsWithoutUploaders?.sort(sortTeamsByPoints), [teamsWithoutUploaders]);
  const limitedTeams = useMemo(() => {
    if (!limit) return sortedTeams;
    return sortedTeams?.slice(0, limit);
  }, [sortedTeams]);

  useEffect(() => {
    if (errorEvent) console.error(errorEvent);
  }, [errorEvent]);

  const isPlayers = event?.mainConfig?.playersOnly ?? false;
  const teamCopy = isPlayers ? t("player") : t("team");

  return (
    <div className={`highscore-table ${className ?? ""}`}>
      <table className="table w-full">
        <thead>
          <tr>
            <th className="pl-4 pr-2">#</th>
            <th className="pr-2 pl-2">{!loadingEvent && teamCopy}</th>
            <th className="pr-2 pl-2"></th>
            <th className="pr-2 pl-2">{t("score")}</th>
          </tr>
        </thead>
        <tbody>
          {limitedTeams.map((team, index) => (
            <TeamRow
              compact={compact}
              active={team.id === teamId}
              key={team.id}
              index={index + 1}
              teamKey={team.id}
              teamName={team.displayName ?? team.name}
              points={Math.round(team?.points)}
              additionalPoints={Math.round(team?.additionalPoints)}
              eventId={eventId}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
